"use client";

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { usePathname, useRouter } from "next/navigation";
import { apiFetch, setAccessToken, getAccessToken } from "@/lib/api";
import type { ApiUser } from "@/lib/types";
import {
  canAccessModule,
  canEditModule,
  normalizeRoleCode,
  type AppModule,
} from "@/lib/roles";

type AuthState = {
  user: ApiUser | null;
  accessToken: string | null;
  isLoading: boolean;
  isBootstrapped: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshMe: () => Promise<void>;
  hasRole: (...roles: number[]) => boolean;
  canAccess: (module: AppModule) => boolean;
  canEdit: (module: AppModule) => boolean;
};

const AuthContext = createContext<AuthState | null>(null);

function getRoleCode(user: ApiUser | null) {
  return normalizeRoleCode(Number(user?.roleCode || 0));
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const router = useRouter();

  const [user, setUser] = useState<ApiUser | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isBootstrapped, setIsBootstrapped] = useState(false);

  const hasRole = useCallback(
    (...roles: number[]) => {
      if (!user) return false;
      const current = getRoleCode(user);
      return roles.map(normalizeRoleCode).includes(current);
    },
    [user],
  );

  const canAccess = useCallback(
    (module: AppModule) => {
      if (!user) return false;
      return canAccessModule(
        getRoleCode(user),
        Array.isArray(user.permissions) ? user.permissions : [],
        module,
      );
    },
    [user],
  );

  const canEdit = useCallback(
    (module: AppModule) => {
      if (!user) return false;
      return canEditModule(
        getRoleCode(user),
        Array.isArray(user.permissions) ? user.permissions : [],
        module,
      );
    },
    [user],
  );

  const refreshMe = useCallback(async () => {
    let access = getAccessToken();

    if (!access) {
      const refresh = await apiFetch<{ accessToken: string }>("/auth/refresh", {
        method: "POST",
        retryOn401: false,
      });

      access = refresh.accessToken;
      setAccessToken(access);
      setToken(access);
    }

    const me = await apiFetch<{ user: ApiUser }>("/auth/me", {
      retryOn401: false,
    });

    setUser(me.user);
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const data = await apiFetch<{ user: ApiUser; accessToken: string }>(
      "/auth/login",
      {
        method: "POST",
        body: JSON.stringify({ email, password }),
        retryOn401: false,
      },
    );

    setAccessToken(data.accessToken);
    setToken(data.accessToken);
    setUser(data.user);
  }, []);

  const logout = useCallback(async () => {
    try {
      await apiFetch<{ ok: boolean }>("/auth/logout", {
        method: "POST",
        retryOn401: false,
      });
    } catch { }

    setAccessToken(null);
    setToken(null);
    setUser(null);
    router.replace("/login");
  }, [router]);

  useEffect(() => {
    let active = true;

    (async () => {
      try {
        const refresh = await apiFetch<{ accessToken: string }>(
          "/auth/refresh",
          {
            method: "POST",
            retryOn401: false,
          },
        );

        if (!active) return;

        setAccessToken(refresh.accessToken);
        setToken(refresh.accessToken);

        const me = await apiFetch<{ user: ApiUser }>("/auth/me", {
          retryOn401: false,
        });

        if (!active) return;
        setUser(me.user);
      } catch (e) {
        console.error("Auth bootstrap refresh failed:", e);

        if (!active) return;
        setAccessToken(null);
        setToken(null);
        setUser(null);
      } finally {
        if (!active) return;
        setIsLoading(false);
        setIsBootstrapped(true);
      }
    })();

    return () => {
      active = false;
    };
  }, []);

  const value = useMemo<AuthState>(
    () => ({
      user,
      accessToken: token,
      isLoading,
      isBootstrapped,
      login,
      logout,
      refreshMe,
      hasRole,
      canAccess,
      canEdit,
    }),
    [
      user,
      token,
      isLoading,
      isBootstrapped,
      login,
      logout,
      refreshMe,
      hasRole,
      canAccess,
      canEdit,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}

export function RequireAuth({ children }: { children: React.ReactNode }) {
  const { user, isLoading, isBootstrapped } = useAuth();
  const router = useRouter();
  const pathname = usePathname();

  useEffect(() => {
    if (!isBootstrapped || isLoading) return;

    if (!user) {
      router.replace(`/login?next=${encodeURIComponent(pathname)}`);
    }


  }, [isBootstrapped, isLoading, user, router, pathname]);

  if (!isBootstrapped || isLoading) return null;
  if (!user) return null;

  return <>{children}</>;
}